import {
  CommentListQuery,
  CommentOrderByWithRelationInput,
  CommentWhereInput,
  QueryMode,
  SortOrder,
} from '@/__generated__/graphql';
import { useLazyQuery, useQuery } from '@apollo/client';
import {
  ActionIcon,
  Anchor,
  Avatar,
  Badge,
  Button,
  Group,
  Paper,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useDebouncedValue, useLocalStorage } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import {
  IconFilterOff,
  IconRefresh,
  IconShare,
  IconSquareCheck,
  IconUser,
  IconX,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { DataTable, DataTableSortStatus } from 'mantine-datatable';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExportToWheelModal } from '../components/ExportToWheelModal';
import {
  commentListDocument,
  commentsByLastKrajPageDocument,
  commentsListPageDocument,
} from '../graphql/queries';
import { KRAJE, postCodeToKraj } from '../utils/kraj';

type CommentRow = CommentListQuery['comments'][number];
type DateRange = [Date | null, Date | null];

const PAGE_ID = '148651926956219';
const POST_ID_PREFIX = `${PAGE_ID}_`;

const COUNTRY_OPTIONS = [
  { value: 'SK', label: 'SK – Slovensko' },
  { value: 'CZ', label: 'CZ – Česko' },
  { value: 'HU', label: 'HU – Maďarsko' },
  { value: 'AT', label: 'AT – Rakúsko' },
  { value: 'DE', label: 'DE – Nemecko' },
  { value: 'PL', label: 'PL – Poľsko' },
];

export const CommentsList = () => {
  const [filterStatus, setFilterStatus] = useLocalStorage<CommentWhereInput>({
    key: 'comment-filterStatus',
    defaultValue: {},
  });

  const [onlyActiveCustomers, setOnlyActiveCustomers] = useLocalStorage({
    key: 'comment-onlyActive',
    defaultValue: false,
  });

  const [onlyWithOrders, setOnlyWithOrders] = useLocalStorage({
    key: 'comment-onlyWithOrders',
    defaultValue: false,
  });

  const [country, setCountry] = useLocalStorage<string | null>({
    key: 'comment-country',
    defaultValue: null,
  });

  const [kraj, setKraj] = useLocalStorage<string | null>({
    key: 'comment-kraj',
    defaultValue: null,
  });

  const [dateRange, setDateRange] = useState<DateRange>([null, null]);

  const [filterStatusDebounced] = useDebouncedValue(filterStatus, 500);

  const where = useMemo<CommentWhereInput>(() => {
    const customerIs: NonNullable<NonNullable<CommentWhereInput['customer']>['is']> = {
      ...(filterStatusDebounced.customer?.is ?? {}),
    };
    if (onlyActiveCustomers) {
      customerIs.active = { equals: true };
    }
    if (onlyWithOrders) {
      customerIs.orders = { some: {} };
    }
    const addressSome: Record<string, unknown> = {};
    if (country) addressSome.country = { equals: country };
    if (Object.keys(addressSome).length > 0) {
      customerIs.addresses = { some: addressSome };
    }
    const result: CommentWhereInput = {
      ...filterStatusDebounced,
      ...(Object.keys(customerIs).length > 0 ? { customer: { is: customerIs } } : {}),
    };
    if (dateRange[0] || dateRange[1]) {
      result.createdAt = {
        ...(dateRange[0] && { gte: dayjs(dateRange[0]).startOf('day').toDate() }),
        ...(dateRange[1] && { lte: dayjs(dateRange[1]).endOf('day').toDate() }),
      };
    }
    return result;
  }, [filterStatusDebounced, onlyActiveCustomers, onlyWithOrders, country, dateRange]);

  const [selected, setSelected] = useState<CommentRow[]>([]);

  const [sortStatus, setSortStatus] = useLocalStorage<DataTableSortStatus<CommentRow>>({
    key: 'comment-sortStatus',
    defaultValue: { columnAccessor: 'createdAt', direction: 'desc' },
  });

  const orderBy = useMemo<CommentOrderByWithRelationInput>(() => {
    const direction = sortStatus.direction as SortOrder;
    switch (sortStatus.columnAccessor) {
      case 'customer.name':
        return { customer: { name: direction } };
      case 'customer.createdAt':
        return { customer: { createdAt: direction } };
      case 'customer._count.orders':
        return { customer: { orders: { _count: direction } } };
      default:
        return { [sortStatus.columnAccessor]: direction };
    }
  }, [sortStatus]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useLocalStorage({
    key: 'comment-pageSize',
    defaultValue: 25,
  });

  const useKrajPath = !!kraj || sortStatus.columnAccessor === 'lastKraj';

  const standardPageQuery = useQuery(commentsListPageDocument, {
    variables: {
      skip: (page - 1) * pageSize,
      take: pageSize,
      where,
      orderBy,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    skip: useKrajPath,
  });

  const krajPageQuery = useQuery(commentsByLastKrajPageDocument, {
    variables: {
      skip: (page - 1) * pageSize,
      take: pageSize,
      where,
      direction: sortStatus.direction as SortOrder,
      filterKraj: kraj,
      sortByKraj: sortStatus.columnAccessor === 'lastKraj',
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    skip: !useKrajPath,
  });

  const data = useKrajPath
    ? krajPageQuery.data
      ? { comments: krajPageQuery.data.commentsByLastKrajPage.items }
      : undefined
    : standardPageQuery.data
      ? { comments: standardPageQuery.data.commentsListPage.items }
      : undefined;
  const loading = useKrajPath ? krajPageQuery.loading : standardPageQuery.loading;
  const refetch = useKrajPath ? krajPageQuery.refetch : standardPageQuery.refetch;

  const [fetchAllMatching, { loading: selectingAll }] = useLazyQuery(commentListDocument, {
    fetchPolicy: 'network-only',
  });
  const [fetchAllMatchingKraj, { loading: selectingAllKraj }] = useLazyQuery(
    commentsByLastKrajPageDocument,
    { fetchPolicy: 'network-only' }
  );

  const totalCount = useKrajPath
    ? (krajPageQuery.data?.commentsByLastKrajPage.totalCount ?? 0)
    : (standardPageQuery.data?.commentsListPage.totalCount ?? 0);

  const updateFilter = <K extends keyof CommentWhereInput>(
    key: K,
    value: CommentWhereInput[K] | undefined
  ) => {
    setFilterStatus((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const updateCustomerNameSearch = (value: string) => {
    setFilterStatus((prev) => {
      const nextIs = { ...(prev.customer?.is ?? {}) };
      if (value.length > 0) {
        nextIs.name = { contains: value, mode: QueryMode.Insensitive };
      } else {
        delete nextIs.name;
      }
      return {
        ...prev,
        customer: Object.keys(nextIs).length > 0 ? { is: nextIs } : undefined,
      };
    });
    setPage(1);
  };

  const handleSelectAllMatching = async () => {
    if (totalCount === 0) return;
    if (useKrajPath) {
      const result = await fetchAllMatchingKraj({
        variables: {
          where,
          skip: 0,
          take: totalCount,
          direction: sortStatus.direction as SortOrder,
          filterKraj: kraj,
          sortByKraj: sortStatus.columnAccessor === 'lastKraj',
        },
      });
      setSelected(result.data?.commentsByLastKrajPage.items ?? []);
    } else {
      const result = await fetchAllMatching({
        variables: { where, orderBy, skip: 0, take: totalCount },
      });
      setSelected(result.data?.comments ?? []);
    }
  };

  const handleClearFilters = () => {
    setFilterStatus({});
    setDateRange([null, null]);
    setOnlyActiveCustomers(false);
    setOnlyWithOrders(false);
    setCountry(null);
    setKraj(null);
    setPage(1);
  };

  const openExport = () => {
    modals.open({
      title: 'Export pre wheelofnames',
      size: '90%',
      children: <ExportToWheelModal comments={selected} />,
    });
  };

  return (
    <Stack>
      {selected.length > 0 && (
        <Paper p="sm" withBorder bg="var(--mantine-color-dark-6)">
          <Group justify="space-between">
            <Group>
              <Badge size="lg" variant="filled">
                {selected.length} vybraných
              </Badge>
              <Tooltip label="Zrušiť výber">
                <ActionIcon variant="subtle" color="red" onClick={() => setSelected([])}>
                  <IconX size={16} />
                </ActionIcon>
              </Tooltip>
            </Group>
            <Button leftSection={<IconShare size={16} />} onClick={openExport}>
              Export pre wheelofnames
            </Button>
          </Group>
        </Paper>
      )}
      <Paper p="md" withBorder>
        <Stack>
          <Group wrap="nowrap">
            <TextInput
              placeholder="ID komentára..."
              value={filterStatus.id?.equals ?? ''}
              onChange={(event) => {
                const v = event.currentTarget.value.trim();
                updateFilter('id', v.length > 0 ? { equals: v } : undefined);
              }}
              flex={1}
            />
            <TextInput
              placeholder="Post ID (suffix)..."
              value={(filterStatus.postId?.equals ?? '').replace(POST_ID_PREFIX, '')}
              onChange={(event) => {
                const v = event.currentTarget.value.trim();
                const stripped = v.startsWith(POST_ID_PREFIX) ? v.slice(POST_ID_PREFIX.length) : v;
                updateFilter(
                  'postId',
                  stripped.length > 0 ? { equals: `${POST_ID_PREFIX}${stripped}` } : undefined
                );
              }}
              flex={1}
            />
            <TextInput
              placeholder="Customer ID..."
              value={filterStatus.customerId?.equals ?? ''}
              onChange={(event) => {
                const v = event.currentTarget.value;
                updateFilter('customerId', v.length > 0 ? { equals: v } : undefined);
              }}
              flex={1}
            />
            <TextInput
              placeholder="Hľadať v komentári..."
              value={filterStatus.body?.contains ?? ''}
              onChange={(event) => {
                const v = event.currentTarget.value;
                updateFilter(
                  'body',
                  v.length > 0 ? { contains: v, mode: QueryMode.Insensitive } : undefined
                );
              }}
              flex={1}
            />
            <TextInput
              placeholder="Meno zákazníka..."
              value={filterStatus.customer?.is?.name?.contains ?? ''}
              onChange={(event) => updateCustomerNameSearch(event.currentTarget.value)}
              flex={1}
            />
            <DatePickerInput
              type="range"
              placeholder="Obdobie..."
              value={dateRange}
              onChange={(value) => {
                setDateRange(value);
                setPage(1);
              }}
              clearable
              allowSingleDateInRange
              flex={1}
            />
          </Group>
          <Group wrap="nowrap">
            <Select
              placeholder="Krajina..."
              data={COUNTRY_OPTIONS}
              value={country}
              onChange={(value) => {
                setCountry(value);
                setPage(1);
              }}
              clearable
              searchable
              flex={1}
            />
            <Select
              placeholder="Kraj..."
              data={KRAJE}
              value={kraj}
              onChange={(value) => {
                setKraj(value);
                setPage(1);
              }}
              clearable
              searchable
              flex={1}
            />
          </Group>
          <Group justify="space-between">
            <Group>
              <Switch
                label="Iba hlavné komentáre (bez odpovedí)"
                checked={filterStatus.parentId?.equals === null}
                onChange={(event) => {
                  updateFilter(
                    'parentId',
                    event.currentTarget.checked ? { equals: null } : undefined
                  );
                }}
              />
              <Switch
                label="Iba aktívni zákazníci"
                checked={onlyActiveCustomers}
                onChange={(event) => {
                  setOnlyActiveCustomers(event.currentTarget.checked);
                  setPage(1);
                }}
              />
              <Switch
                label="Iba s objednávkou"
                checked={onlyWithOrders}
                onChange={(event) => {
                  setOnlyWithOrders(event.currentTarget.checked);
                  setPage(1);
                }}
              />
            </Group>
            <Group>
              <Button
                variant="subtle"
                leftSection={<IconFilterOff size={16} />}
                onClick={handleClearFilters}
              >
                Vyčistiť filter
              </Button>
              <Tooltip label="Vyberie všetky komentáre vyhovujúce filtru naprieč stránkami">
                <Button
                  variant="light"
                  leftSection={<IconSquareCheck size={16} />}
                  loading={selectingAll || selectingAllKraj}
                  disabled={totalCount === 0}
                  onClick={handleSelectAllMatching}
                >
                  Vybrať všetky ({totalCount})
                </Button>
              </Tooltip>
            </Group>
          </Group>
        </Stack>
      </Paper>
      <DataTable
        withTableBorder
        striped
        highlightOnHover
        borderRadius="sm"
        minHeight={200}
        records={data?.comments ?? []}
        fetching={loading}
        totalRecords={totalCount}
        recordsPerPage={pageSize}
        page={page}
        onPageChange={setPage}
        recordsPerPageOptions={[10, 25, 50, 100]}
        onRecordsPerPageChange={setPageSize}
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        selectedRecords={selected}
        onSelectedRecordsChange={setSelected}
        columns={[
          {
            accessor: 'body',
            title: 'Komentár',
            sortable: true,
            render: ({ body }) => (
              <Text size="sm" lineClamp={2} maw={420}>
                {body}
              </Text>
            ),
          },
          {
            accessor: 'customer.name',
            title: 'Zákazník',
            sortable: true,
            render: ({ customer }) => (
              <Group gap="xs" wrap="nowrap">
                <Avatar src={customer.avatar} size="sm" radius="xl" />
                <Stack gap={0}>
                  <Anchor component={Link} to={`/customer/${customer.id}`} size="sm">
                    {customer.name}
                  </Anchor>
                  {customer.email && (
                    <Text size="xs" c="dimmed">
                      {customer.email}
                    </Text>
                  )}
                </Stack>
              </Group>
            ),
          },
          {
            accessor: 'customer.createdAt',
            title: 'Zákazník od',
            sortable: true,
            render: ({ customer }) => dayjs(customer.createdAt).format('DD.MM.YYYY'),
          },
          {
            accessor: 'customer._count.orders',
            title: 'Počet obj.',
            textAlign: 'right',
            sortable: true,
            render: ({ customer }) => customer._count.orders,
          },
          {
            accessor: 'lastCountry',
            title: 'Krajina',
            render: ({ customer }) => customer.addresses?.[0]?.country ?? '—',
          },
          {
            accessor: 'lastKraj',
            title: 'Kraj',
            sortable: true,
            render: ({ customer }) => postCodeToKraj(customer.addresses?.[0]?.postCode) ?? '—',
          },
          {
            accessor: 'lastPostCode',
            title: 'PSČ',
            render: ({ customer }) => customer.addresses?.[0]?.postCode ?? '—',
          },
          {
            accessor: 'postId',
            title: 'Post ID',
            sortable: true,
            render: ({ postId }) => (
              <Text size="xs" ff="monospace">
                {postId}
              </Text>
            ),
          },
          {
            accessor: 'createdAt',
            title: 'Dátum',
            sortable: true,
            render: ({ createdAt }) => dayjs(createdAt).format('DD.MM.YYYY HH:mm:ss'),
          },
          {
            accessor: 'actions',
            textAlign: 'right',
            title: (
              <Group justify="flex-end">
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="green"
                  onClick={(e) => {
                    e.stopPropagation();
                    void refetch();
                  }}
                >
                  <IconRefresh size={16} />
                </ActionIcon>
              </Group>
            ),
            render: ({ customer }) => (
              <Group justify="flex-end">
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  component={Link}
                  to={`/customer/${customer.id}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <IconUser size={16} />
                </ActionIcon>
              </Group>
            ),
          },
        ]}
      />
    </Stack>
  );
};
